import type { SavedBacktestMeta, BacktestSnapshot } from "../state/types";
import { getAuthHeaders, triggerUnauthorized } from "../auth/token";

const BASE = "/backtests";

function authHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", ...getAuthHeaders() };
}

async function checkAuth(res: Response): Promise<void> {
  if (res.status === 401) {
    triggerUnauthorized();
    throw new Error("Session expired or invalid. Please sign in again.");
  }
}

export async function listBacktests(): Promise<SavedBacktestMeta[]> {
  const res = await fetch(BASE, { headers: getAuthHeaders() });
  await checkAuth(res);
  if (!res.ok) {
    const text = await res.text();
    let msg: string;
    try {
      const body = JSON.parse(text) as { detail?: string };
      msg = body.detail ?? text;
    } catch {
      msg = text || `List failed (${res.status})`;
    }
    throw new Error(msg);
  }
  const data = (await res.json()) as { backtests: SavedBacktestMeta[] };
  return data.backtests ?? [];
}

export async function getBacktest(id: string): Promise<BacktestSnapshot> {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
    headers: getAuthHeaders(),
  });
  await checkAuth(res);
  if (res.status === 404) {
    throw new Error("NOT_FOUND");
  }
  if (!res.ok) {
    const text = await res.text();
    let msg: string;
    try {
      const body = JSON.parse(text) as { detail?: string };
      msg = body.detail ?? text;
    } catch {
      msg = text || `Load failed (${res.status})`;
    }
    throw new Error(msg);
  }
  return (await res.json()) as BacktestSnapshot;
}

export type SaveBacktestResult = { snapshot: BacktestSnapshot; conflict?: boolean };

export async function saveBacktest(
  id: string,
  payload: BacktestSnapshot
): Promise<SaveBacktestResult> {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  await checkAuth(res);
  if (res.status === 409) {
    return { snapshot: payload, conflict: true };
  }
  if (res.status === 400 || !res.ok) {
    const text = await res.text();
    let msg: string;
    try {
      const body = JSON.parse(text) as { detail?: string };
      msg = body.detail ?? text;
    } catch {
      msg = text || `Save failed (${res.status})`;
    }
    throw new Error(msg);
  }
  const snapshot = (await res.json()) as BacktestSnapshot;
  return { snapshot, conflict: false };
}

export async function deleteBacktest(id: string): Promise<void> {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  await checkAuth(res);
  // 204 success; 404 treat as success (idempotent)
  if (res.status === 404 || res.status === 204) return;
  if (!res.ok) {
    const text = await res.text();
    let msg: string;
    try {
      const body = JSON.parse(text) as { detail?: string };
      msg = body.detail ?? text;
    } catch {
      msg = text || `Delete failed (${res.status})`;
    }
    throw new Error(msg);
  }
}
